import type {
	OrgaAdminUser,
	StudentKlausurDetailedDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";

/**
 * Defines the complete state shape for the korrektur status table.
 * This interface centralizes all state properties, making it easier to manage
 * and understand the component's data at a glance.
 */
export interface KorrekturTableState {
	// Raw Data passed from parent components
	studentKlausuren: StudentKlausurDetailedDTO[];
	users: OrgaAdminUser[];
	klausurTitleMap: Map<string, string>;
	availableStepNumbers: number[];

	// UI-driven state for filtering and searching
	searchTerm: string;
	selectedStatus: string;

	// State for row selection
	selectedStudentKlausurIds: Set<string>;

	// State to track the application of initial deep-link filters
	hasAppliedInitialFilters: boolean;
}

/**
 * Defines all possible actions that can be dispatched to update the table's state.
 * Using a discriminated union ensures type safety for action payloads.
 */
export type KorrekturTableAction =
	| { type: "SET_SEARCH_TERM"; payload: string }
	| { type: "SET_SELECTED_STATUS"; payload: string }
	| { type: "TOGGLE_KLAUSUR_SELECTION"; payload: string }
	| { type: "SELECT_ALL_CURRENT_PAGE"; payload: string[] }
	| { type: "CLEAR_SELECTION" }
	| { type: "SET_SELECTED_KLAUSUR_IDS"; payload: Set<string> }
	| { type: "SET_HAS_APPLIED_INITIAL_FILTERS"; payload: boolean };

/**
 * The reducer function for the korrektur status table. It handles all state transitions.
 * @param state - The current state.
 * @param action - The action to be performed.
 * @returns The new state.
 */
export function korrekturTableReducer(
	state: KorrekturTableState,
	action: KorrekturTableAction,
): KorrekturTableState {
	switch (action.type) {
		// When filters change, reset to the first page to avoid showing an empty page.
		case "SET_SEARCH_TERM":
			return { ...state, searchTerm: action.payload };
		case "SET_SELECTED_STATUS":
			return { ...state, selectedStatus: action.payload };

		// Handle row selection logic for single and bulk actions.
		case "TOGGLE_KLAUSUR_SELECTION": {
			const newSelected = new Set(state.selectedStudentKlausurIds);
			if (newSelected.has(action.payload)) {
				newSelected.delete(action.payload);
			} else {
				newSelected.add(action.payload);
			}
			return { ...state, selectedStudentKlausurIds: newSelected };
		}
		case "SELECT_ALL_CURRENT_PAGE": {
			const newSelected = new Set(state.selectedStudentKlausurIds);
			// Use forEach for side effects like adding to a Set.
			action.payload.map((id) => newSelected.add(id));
			return { ...state, selectedStudentKlausurIds: newSelected };
		}
		case "CLEAR_SELECTION":
			return { ...state, selectedStudentKlausurIds: new Set() };
		case "SET_SELECTED_KLAUSUR_IDS":
			return { ...state, selectedStudentKlausurIds: action.payload };

		// Handle UI state for the bulk assignment panel.
		case "SET_HAS_APPLIED_INITIAL_FILTERS":
			return { ...state, hasAppliedInitialFilters: action.payload };
		default:
			return state;
	}
}
